import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Brain, Rocket, Target, Clock, Sparkles, Bot } from "lucide-react";

export default function GetStartedPage() {
  const navigate = useNavigate();

  const aiFeatures = [
    {
      icon: Brain,
      title: "Smart Event Recommendations",
      description: "Our AI analyzes your mission and past events to suggest high-impact volunteering opportunities that align with your goals."
    },
    {
      icon: Bot,
      title: "AI-Powered Content Generation",
      description: "Generate professional event descriptions, impact reports, and promotional content with our advanced AI writing assistant."
    },
    {
      icon: Target,
      title: "Intelligent Corporate Matching",
      description: "Let our AI match you with corporate partners whose values and interests align perfectly with your mission."
    },
    {
      icon: Clock,
      title: "Automated Scheduling",
      description: "AI-driven scheduling system that optimizes event timing based on historical participation data and volunteer preferences."
    },
    {
      icon: Sparkles,
      title: "Smart Impact Metrics",
      description: "Automatically track and visualize your impact with AI-powered analytics and reporting tools."
    },
    {
      icon: Rocket,
      title: "Predictive Analytics",
      description: "Forecast volunteer turnout and resource needs using machine learning algorithms trained on historical data."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent">
              Transform Your Nonprofit with AI-Powered Tools
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Stay ahead of the curve with CauseConnect's cutting-edge AI technology. 
              Streamline operations, increase impact, and focus on what matters most,your mission.
            </p>
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-white px-8"
              onClick={() => navigate("/signup")}
            >
              Get Started for Free
            </Button>
          </div>
        </div>
      </section>

      {/* AI Features Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Industry-Leading AI Tools for Nonprofits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {aiFeatures.map((feature, index) => (
              <div 
                key={index}
                className="p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow bg-white"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Why Choose CauseConnect's AI Platform?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our AI-powered platform helps you work smarter, not harder, 
              delivering measurable results and meaningful impact.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Save Time and Resources</h3>
                  <p className="text-gray-600">
                    Automate routine tasks and administrative work, letting you focus on strategic initiatives 
                    and relationship building.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Data-Driven Decisions</h3>
                  <p className="text-gray-600">
                    Make informed decisions with AI-powered insights and analytics that help you understand 
                    and improve your impact.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Scale Your Impact</h3>
                  <p className="text-gray-600">
                    Reach more volunteers and corporate partners with AI-optimized outreach and 
                    engagement strategies.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-8 rounded-2xl">
              <div className="space-y-6">
                <h3 className="text-2xl font-bold mb-4">Platform Benefits</h3>
                <ul className="space-y-4">
                  <li className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-primary text-sm">✓</span>
                    </span>
                    <span>30+ hours saved per event</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-primary text-sm">✓</span>
                    </span>
                    <span>50% reduction in administrative work</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-primary text-sm">✓</span>
                    </span>
                    <span>2x increase in corporate partner engagement</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-primary text-sm">✓</span>
                    </span>
                    <span>AI-powered impact reporting and analytics</span>
                  </li>
                </ul>

                <Button 
                  className="w-full mt-6 bg-primary hover:bg-primary/90 text-white"
                  onClick={() => navigate("/signup")}
                >
                  Sign Up Free
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}